import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { Checkbox } from '@hub/checkbox'
import { H3, H4 } from '@hub/heading'
import { Text } from '@hub/text'
import { Stack } from '@hub/stack'
import {
  ConsentInput,
  OptInMarketingDocument,
  OptOutMarketingDocument,
  UpdateCustomerConsentDocument,
} from '@scentregroup/shared/graphql'
import { useSafeMutation } from '../../lib/apollo-client'
import { userAtom } from '../../lib/atoms/user-info'
import SpinnerDelayed from '../spinner-delayed'
import { onError } from '../../lib/on-error'
import useNotification from '../../lib/use-notification'
import { ERROR_UPDATE_PREFERENCES } from '../../lib/error-messages'
import { CONFIG_KEYS, getClientConfigBool } from '@scentregroup/shared/config'
import { ConsentKeys, UPDATE_CONSENT_CHANNEL } from '../../types/consent'

const Preferences: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { openNotification, closeNotification } = useNotification()

  const [isSavingEmailMarketing, setIsSavingEmailMarketing] = useState(false)
  const [isSavingPersonalisedAds, setIsSavingPersonalisedAds] = useState(false)

  const user = useRecoilValue(userAtom)
  const [marketingEmailsChecked, setMarketingEmailsChecked] =
    useState<boolean>()
  const [targetedAdsChecked, setTargetedAdsChecked] = useState<boolean>()

  const [optInMarketing] = useSafeMutation(OptInMarketingDocument)
  const [optOutMarketing] = useSafeMutation(OptOutMarketingDocument)
  const [updateConsent] = useSafeMutation(UpdateCustomerConsentDocument)

  const updateConsentDecision = useCallback(
    (key: string, value: boolean) => {
      const consent: ConsentInput = {
        emailMarketing: null,
        pushNotificationMarketing: null,
        targetedAdvertising: null,
        [key]: value,
      }

      return updateConsent({
        variables: {
          input: {
            channel: UPDATE_CONSENT_CHANNEL,
            consent,
          },
        },
      })
    },
    [updateConsent]
  )

  // SUGG This logic can be reversed once enabling is no longer a noop
  const isGraphQLConsentDisabled = !getClientConfigBool(
    CONFIG_KEYS.NEXT_PUBLIC_FEAT_FLAG_GRAPHQL_CONSENT
  )
  const isTargetedAdsConsentEnabled =
    getClientConfigBool(
      CONFIG_KEYS.NEXT_PUBLIC_FEAT_FLAG_CONSENT_TARGETED_ADS
    ) && !isGraphQLConsentDisabled

  const [consentEmailMarketing, consentTargetedAds] = useMemo(() => {
    if (!user.data) {
      return [false, false]
    }

    if (isGraphQLConsentDisabled) {
      return [user.data?.westfieldPlus?.emailMarketingOptIn, false]
    }

    return [ConsentKeys.EMAIL_MARKETING, ConsentKeys.TARGETED_ADVERTISING].map(
      consentKey => Boolean(user.data?.consent?.[consentKey])
    )
  }, [isGraphQLConsentDisabled, user.data])

  const onChangeEmailMarketing = useCallback(
    async (event: any): Promise<void> => {
      closeNotification()
      setIsSavingEmailMarketing(true)
      const checked = event.target.checked

      try {
        if (isGraphQLConsentDisabled) {
          if (checked) {
            await optInMarketing()
          } else {
            await optOutMarketing()
          }
        } else {
          await updateConsentDecision(ConsentKeys.EMAIL_MARKETING, checked)
        }

        setMarketingEmailsChecked(checked)
      } catch (err) {
        onError(err, openNotification, ERROR_UPDATE_PREFERENCES)
      } finally {
        setIsSavingEmailMarketing(false)
        if (user.refetch) {
          await user.refetch()
        }
      }
    },
    [
      isGraphQLConsentDisabled,
      optInMarketing,
      optOutMarketing,
      openNotification,
      closeNotification,
      updateConsentDecision,
      user,
    ]
  )

  const onChangeTargetedAds = useCallback(
    async (event: any): Promise<void> => {
      closeNotification()
      setIsSavingPersonalisedAds(true)
      const checked = event.target.checked

      try {
        await updateConsentDecision(ConsentKeys.TARGETED_ADVERTISING, checked)
        setTargetedAdsChecked(checked)
      } catch (err) {
        onError(err, openNotification, ERROR_UPDATE_PREFERENCES)
      } finally {
        setIsSavingPersonalisedAds(false)
        if (user.refetch) {
          await user.refetch()
        }
      }
    },
    [closeNotification, updateConsentDecision, openNotification, user]
  )

  useEffect(() => {
    setMarketingEmailsChecked(consentEmailMarketing)
  }, [consentEmailMarketing, setMarketingEmailsChecked])

  useEffect(() => {
    setTargetedAdsChecked(consentTargetedAds)
  }, [consentTargetedAds, setTargetedAdsChecked])

  if (!user.data) {
    return <SpinnerDelayed />
  }

  return (
    <Stack gap={'spacing-lg'}>
      <H3>Preferences</H3>
      <Text>
        Receive updates about new Westfield services and their benefits, events
        and offers
      </Text>
      <Checkbox
        isChecked={marketingEmailsChecked}
        onChange={onChangeEmailMarketing}
        isLoading={isSavingEmailMarketing}
      >
        Receive marketing updates by email
      </Checkbox>
      {isTargetedAdsConsentEnabled && (
        <Stack gap={'spacing-lg'}>
          <H4>Personalised Ads</H4>
          <Text>
            Receive personalised ads that are relevant to you based on your
            Westfield browsing history
          </Text>
          <Checkbox
            isChecked={targetedAdsChecked}
            onChange={onChangeTargetedAds}
            isLoading={isSavingPersonalisedAds}
          >
            Receive personalised ads
          </Checkbox>
        </Stack>
      )}
    </Stack>
  )
}

export default Preferences
